import React, { useRef, useEffect, useState } from 'react';
import { Form, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';

const LightningArresterForm = () => {
    const dispatch = useDispatch();
    const { viewFormData } = useSelector((state) => state.checklist);
    const { formId, stationId } = useParams();
    const sigCanvas = useRef(null);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [currentSignature, setCurrentSignature] = useState({ role: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [isEditable, setIsEditable] = useState(true);

    const { register, control, handleSubmit, setValue, getValues } = useForm({
        defaultValues: {
            station_id: stationId || '',
            project_name: '',
            serial_number: '',
            height_structure: '',
            la_make_model: '',
            roof_type: '',
            visual_checks: Array(13).fill({ status: '', remark: '' }),
            observations: { a: '', b: '', c: '' },
            comments: {
                contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
            },
            status: 'draft',
        },
    });

    const formValues = useWatch({ control });

    const autoSave = React.useCallback(
        _.debounce((data) => {
            if (!isEditable) return;
            const payload = {
                form_data: {
                    status: data.status,
                    form_id: formId,
                    formName: 'lightning_arrester',
                    station_id: data.station_id,
                    project_name: data.project_name,
                    serial_number: data.serial_number,
                    height_structure: data.height_structure,
                    la_make_model: data.la_make_model,
                    roof_type: data.roof_type,
                    visual_checks: data.visual_checks,
                    observations: data.observations,
                    comments: data.comments,
                },
            };
            dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
        }, 2000),
        [dispatch, formId]
    );

    useEffect(() => {
        autoSave(formValues);
    }, [formValues]);

    useEffect(() => {
        if (formId) {
            dispatch({ type: VIEW_FORM, payload: { formId, formName: 'lightning_arrester' } });
        }
        setIsLoading(false);
    }, [dispatch, formId]);

    useEffect(() => {
        if (viewFormData) {
            const data = viewFormData || {};
            setIsEditable(!['pending', 'approved'].includes(data.status));
            setValue('station_id', data.station_id || stationId || '');
            setValue('project_name', data.project_name || '');
            setValue('serial_number', data.serial_number || '');
            setValue('height_structure', data.height_structure || '');
            setValue('la_make_model', data.la_make_model || '');
            setValue('roof_type', data.roof_type || '');
            setValue('visual_checks', Array.isArray(data.visual_checks)
                ? data.visual_checks
                : Array(13).fill({ status: '', remark: '' }));
            setValue('observations', data.observations || { a: '', b: '', c: '' });
            setValue('comments', data.comments || {
                contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
            });
            setValue('status', data.status || 'draft');
        }
    }, [viewFormData, stationId, setValue]);

    const handleImageUpload = (e, role) => {
        if (!isEditable) return;
        const file = e.target.files[0];
        const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB limit
        if (file) {
            if (file.size > maxSizeInBytes) {
                alert('File size exceeds 50kb. Please upload a smaller image.');
                handleResetSignature(role);
                sigCanvas.current?.clear();
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setValue(`comments.${role}.signature`, reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSignatureTypeChange = (role, type) => {
        if (!isEditable) return;
        setValue(`comments.${role}.signatureType`, type);
        setValue(`comments.${role}.signature`, '');
        if (type === 'digital') {
            setCurrentSignature({ role });
            setShowModal(true);
        }
    };

    const handleResetSignature = (role) => {
        if (!isEditable) return;
        setValue(`comments.${role}.signature`, '');
        setValue(`comments.${role}.signatureType`, 'upload');
    };

    const handleSaveSignature = () => {
        if (!isEditable) return;
        if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
            const signatureData = sigCanvas.current.toDataURL();
            setValue(`comments.${currentSignature.role}.signature`, signatureData);
        }
        setShowModal(false);
        sigCanvas.current?.clear();
    };

    const handleClearSignature = () => {
        if (!isEditable) return;
        sigCanvas.current?.clear();
    };

    const handleCloseModal = () => {
        if (!isEditable) {
            setShowModal(false);
            return;
        }
        handleResetSignature(currentSignature.role);
        setShowModal(false);
        sigCanvas.current?.clear();
    };

    const onSubmit = (status) => (data) => {
        if (!isEditable) {
            toast.info('Form is not editable in pending or approved status');
            return;
        }
        const payload = {
            form_data: {
                status: status,
                form_id: formId,
                formName: 'lightning_arrester',
                station_id: data.station_id,
                project_name: data.project_name,
                serial_number: data.serial_number,
                height_structure: data.height_structure,
                la_make_model: data.la_make_model,
                roof_type: data.roof_type,
                visual_checks: data.visual_checks,
                observations: data.observations,
                comments: data.comments,
            },
        };
        dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
        toast.success(`Form ${status === 'pending' ? 'Submitted' : 'Saved as Draft'} Successfully`);
        navigate(-1);
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="page-body container-xl">
            <h2 className="page-title mb-4">Lightning Arrester - Site Acceptance Testing</h2>
            {!isEditable && (
                <div className="alert alert-warning mb-4">
                    This form is in View Mode Only.
                </div>
            )}
            <Form>
                <h3>1. Equipment Details</h3>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Station ID</Form.Label>
                            <Form.Control type="text" {...register('station_id')} readOnly />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Project Name</Form.Label>
                            <Form.Control type="text" {...register('project_name')} disabled={!isEditable} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Serial Number</Form.Label>
                            <Form.Control type="text" {...register('serial_number')} disabled={!isEditable} />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Height of Structure/Mast</Form.Label>
                            <Form.Control type="text" {...register('height_structure')} disabled={!isEditable} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>LA Make & Model</Form.Label>
                            <Form.Control type="text" {...register('la_make_model')} disabled={!isEditable} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Type of Roof for Installation</Form.Label>
                            <Form.Select {...register('roof_type')} disabled={!isEditable}>
                                <option value="">Select</option>
                                <option value="Flat roof">Flat roof</option>
                                <option value="Metal roof">Metal roof</option>
                                <option value="Separate Mast">Separate Mast</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <h3 className="mt-4">2. Visual Checks</h3>
                <Table bordered>
                    <thead>
                        <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
                    </thead>
                    <tbody>
                        {[
                            'Is the air terminal free from corrosion, dents, or any damage?',
                            'Is the physical condition of the down conductor satisfactory?',
                            'Is the air terminal located in an open area, or is it under the shadow of any building, tower, or mast?',
                            'Is separate earth connection provided?',
                            'What is the condition and number of earth connections?',
                            'Is the routing of the down conductor appropriate to prevent side flash issues?',
                            'Are insulation supports provided for the down conductors?',
                            'Is there a test link present?',
                            'Is there one test link provided for each down conductor?',
                            'Is surge protection provided for field instruments, telecommunication equipment, etc.?',
                            'Is the air terminal installed as per the marked locations on the plan/elevation drawing?',
                            'Is a surge counter (digital or analogue) present?',
                            'Are all joints properly fixed as per the typical installation guidelines?',
                        ].map((desc, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{desc}</td>
                                <td>
                                    <Form.Check
                                        inline
                                        label="Yes"
                                        type="radio"
                                        name={`visual_checks_${index}`}
                                        value="Yes"
                                        {...register(`visual_checks.${index}.status`)}
                                        disabled={!isEditable}
                                    />
                                    <Form.Check
                                        inline
                                        label="No"
                                        type="radio"
                                        name={`visual_checks_${index}`}
                                        value="No"
                                        {...register(`visual_checks.${index}.status`)}
                                        disabled={!isEditable}
                                    />
                                    <Form.Check
                                        inline
                                        label="NA"
                                        type="radio"
                                        name={`visual_checks_${index}`}
                                        value="NA"
                                        {...register(`visual_checks.${index}.status`)}
                                        disabled={!isEditable}
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        type="text"
                                        {...register(`visual_checks.${index}.remark`)}
                                        disabled={!isEditable}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                <h3 className="mt-4">3. Observations during Commissioning / Testing (If Any)</h3>
                {['a', 'b', 'c'].map((key) => (
                    <Form.Group className="mb-3" key={key}>
                        <Form.Label>{key.toUpperCase()}.</Form.Label>
                        <Form.Control
                            type="text"
                            {...register(`observations.${key}`)}
                            disabled={!isEditable}
                        />
                    </Form.Group>
                ))}

                <h3 className="mt-4">4. Comments</h3>
                <Table bordered>
                    <thead>
                        <tr><th></th><th>Engineer - Contractor</th><th>Engineer - Charge Zone</th><th>Quality - Charge Zone</th><th>Optional</th></tr>
                    </thead>
                    <tbody>
                        {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                            <tr key={field}>
                                <td>{field}</td>
                                {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                                    <td key={role}>
                                        {field === 'Date' ? (
                                            <Form.Control
                                                type="date"
                                                {...register(`comments.${role}.${field.toLowerCase()}`)}
                                                disabled={!isEditable}
                                            />
                                        ) : field === 'Signature' ? (
                                            <div>
                                                <Form.Select
                                                    {...register(`comments.${role}.signatureType`)}
                                                    onChange={(e) => handleSignatureTypeChange(role, e.target.value)}
                                                    className="mb-2"
                                                    disabled={!isEditable}
                                                >
                                                    <option value="upload">Image Upload</option>
                                                    <option value="digital">Digital Sign</option>
                                                </Form.Select>
                                                {getValues(`comments.${role}.signatureType`) === 'upload' && (
                                                    <Form.Control
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleImageUpload(e, role)}
                                                        className="mb-2"
                                                        disabled={!isEditable}
                                                    />
                                                )}
                                                {getValues(`comments.${role}.signature`) && (
                                                    <div>
                                                        <img
                                                            src={getValues(`comments.${role}.signature`)}
                                                            alt="Signature"
                                                            style={{ maxWidth: '100px', marginTop: '5px' }}
                                                        />
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleResetSignature(role)}
                                                            className="mt-2"
                                                            disabled={!isEditable}
                                                        >
                                                            Reset Signature
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                {...register(`comments.${role}.${field.toLowerCase()}`)}
                                                disabled={!isEditable}
                                            />
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>

                <Button
                    variant="success"
                    onClick={handleSubmit(onSubmit('pending'))}
                    className="mt-4"
                    disabled={!isEditable}
                >
                    Submit Form
                </Button>
                <Button
                    variant="secondary"
                    style={{ marginLeft: '10px' }}
                    onClick={handleSubmit(onSubmit('draft'))}
                    className="mt-4"
                    disabled={!isEditable}
                >
                    Save As Draft
                </Button>

                <Modal show={showModal && isEditable} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Draw Signature</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <SignatureCanvas
                            ref={sigCanvas}
                            penColor="black"
                            canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
                        />
                        <div className="mt-2">
                            <Button variant="secondary" onClick={handleClearSignature} className="me-2" disabled={!isEditable}>
                                Clear
                            </Button>
                            <Button variant="primary" onClick={handleSaveSignature} disabled={!isEditable}>
                                Save Signature
                            </Button>
                        </div>
                    </Modal.Body>
                </Modal>
            </Form>
        </div>
    );
};

export default LightningArresterForm;